import React, { useState, useCallback, memo, useMemo } from 'react';
import { renderIcon } from '../../../FlowchartView/utils/renderIcon';
import IconPicker from '../../../shared/IconPicker';
import StatusDropdown from '../../../shared/StatusDropdown';
import PriorityDropdown from '../../../FlowchartView/TaskNode/PriorityDropdown';
import DifficultyDropdown from '../../../FlowchartView/TaskNode/DifficultyDropdown';
import { QuestMarker } from '../../../FlowchartView/components/FlowchartHUD/icons';
import { CampaignItemIcon } from '../../../../Sidebar/icons';
import DecisionOptionsModal, { DEFAULT_OPTIONS } from '../../../FlowchartView/TaskNode/DecisionOptionsModal';
import { getStatusConfig } from '../../../FlowchartView/utils/statusUtils';

import ActionButtons from './components/ActionButtons';
import TodoModals from './components/TodoModals';
import MetaBadges from './components/MetaBadges';
import DecisionToggle from './components/DecisionToggle';
import WaitingForIndicator from './components/WaitingForIndicator';
import CategoryTags from './components/CategoryTags';
import TodoCustomFields from './components/TodoCustomFields';
import { formatDueDate, getDueDateClass } from './utils/dateUtils';
import { useTaskEditing } from './hooks/useTaskEditing';
import { useIconPicker } from './hooks/useIconPicker';

import './styles/index.css';

/**
 * TodoItem Component
 * 
 * A clean, simple todo item with:
 * - Checkbox for completion
 * - Indentation based on depth
 * - Icon and name
 * - Priority and due date (inline, subtle)
 */
const TodoItem = memo(({
  task,
  depth = 0,
  isSubtask = false,
  isParent = false,
  nestedSprint = null,
  hasNestedCampaign = false,
  blockingDependencies = [],
  parentMissionName = '',
  parentCampaignName = '',
  parentMissionId = null,
  parentSprintId = null,
  onNavigateToParentMission,
  isLocked = false,
  isExcluded = false,
  lockReason = '',
  statuses = [],
  allTags = [],
  allTasks = [],
  isChecked = false,
  onOpenDetails,
  onOpenAttachments,
  onStatusChange,
  onPriorityChange,
  onDifficultyChange,
  onDueDateChange,
  onRename,
  onDelete,
  onArchive,
  onUnarchive,
  onDependencyChange,
  onConditionsChange,
  onDrillIn,
  onFocusTask,
  onAddSubtask,
  onIconChange,
  // Status management
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  onSetStatusAutoSync,
  // Selection for Fokus mode
  isSelected = false,
  onToggleSelect,
  // Element visibility
  elementVisibility = {},
  // View navigation (for "Open in view" dropdown)
  currentView = 'todolist',
  customViews = [],
  activeCustomViewId = null,
  sourceCampaignId = null, // For nested items - the campaign where this item belongs
  onViewChange,
  onSelectCustomView,
  onGoToTaskInView,
  // Decision handlers
  onDecisionAnswerChange,
  onDecisionOptionsChange,
  // Custom fields
  customFields = [],
  onCustomFieldValueChange,
  // Add mission to nested sprint (for parent items)
  onAddMissionToSprint,
  // Deliverables
  onSubmitDeliverable,
  nestedSprints = [],
  allSprints = [],
}) => {
  const [showActions, setShowActions] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDecisionOptionsModal, setShowDecisionOptionsModal] = useState(false);
  
  // Decision state
  const isDecision = task?.nodeType === 'decision';
  const decisionOptions = task?.options || DEFAULT_OPTIONS;
  const decisionAnswer = task?.answer || null;
  
  // Derived state
  const hasBlockingDependencies = !isSubtask && blockingDependencies.length > 0;
  const taskName = task.name || task.text || '';
  const isPlaceholderName = !taskName || taskName === 'ENTER MISSION NAME' || taskName === 'New Decision';
  const placeholderHint = isDecision ? 'Type a decision question...' : 'Type a mission name...';
  const dueDate = !isSubtask ? task.dueDate : null;
  const dueDateDisplay = formatDueDate(dueDate);
  const dueDateClass = getDueDateClass(dueDate);


  // Use editing hook
  const {
    isEditing,
    editValue,
    setEditValue,
    inputRef,
    nameSpanRef,
    handleStartEdit,
    handleFinishEdit,
    handleKeyDown,
  } = useTaskEditing({
    taskId: task.id,
    taskName,
    isSubtask,
    isPlaceholderName,
    onRename,
  });

  // Use icon picker hook
  const {
    showIconPicker,
    iconPickerPosition,
    iconButtonRef,
    handleIconClick,
    handleIconChange,
    closeIconPicker,
  } = useIconPicker({
    taskId: task.id,
    onIconChange,
  });

  // Decision options change handler
  const handleDecisionOptionsChange = useCallback((newOptions) => {
    onDecisionOptionsChange?.(task.id, newOptions);
    if (decisionAnswer && !newOptions.find(opt => opt.id === decisionAnswer)) {
      onDecisionAnswerChange?.(task.id, null);
    }
  }, [task.id, decisionAnswer, onDecisionAnswerChange, onDecisionOptionsChange]);

  // Row styling: locked tint can apply with excluded; icon is excluded OR lock, never both
  const showRowLocked = !isSubtask && isLocked && !isChecked;
  const showExcludedIcon = !isSubtask && isExcluded;
  const showLockIcon = !isSubtask && isLocked && !isExcluded && !isChecked;

  // Get status color for the wrapper styling
  const statusConfig = useMemo(() => {
    return getStatusConfig(task.status, statuses);
  }, [task.status, statuses]);

  // Handle row click for selection (only when clicking non-interactive areas)
  // PERFORMANCE: Instantly update DOM for visual feedback before React state updates
  const handleRowClick = useCallback((e) => {
    // Don't toggle selection if clicking on interactive elements
    // These elements handle their own clicks
    if (e.target.closest('button, input, textarea, a, [role="button"], .status-dropdown, .priority-dropdown, .difficulty-dropdown, .todo-item__dropdown-wrapper, .todo-item__status-wrapper')) {
      return;
    }
    // Only toggle selection for non-subtasks when selection is enabled
    if (!isSubtask && onToggleSelect && elementVisibility.select !== false) {
      // Check if shift or ctrl/cmd is held for multi-select
      const isMultiSelect = e.shiftKey || e.ctrlKey || e.metaKey;
      
      // Instant DOM feedback for snappy selection
      const todoEl = e.currentTarget;
      if (isMultiSelect) {
        // Toggle this item's selection class
        todoEl.classList.toggle('todo-item--selected');
      } else {
        // Clear all selections and select just this one
        document.querySelectorAll('.todo-item--selected').forEach(el => {
          el.classList.remove('todo-item--selected');
        });
        todoEl.classList.add('todo-item--selected');
      }
      
      onToggleSelect(task.id, { addToSelection: isMultiSelect });
    }
  }, [isSubtask, onToggleSelect, elementVisibility.select, task.id]);

  // Determine if row is selectable (for cursor styling)
  const isSelectable = !isSubtask && onToggleSelect && elementVisibility.select !== false;

  return (
    <>
      {/* NOTE: 'todo-item--selected' is NOT included here - it's managed via DOM manipulation
          in handleRowClick for instant visual feedback. React state still tracks selection
          for other purposes (bulk actions, etc.) but the visual class is DOM-driven. */}
      <div 
        className={`todo-item ${isChecked ? 'todo-item--checked' : ''} ${isSubtask ? 'todo-item--subtask' : ''} ${isParent ? 'todo-item--parent' : ''} ${task.archived ? 'todo-item--archived' : ''} ${showExcludedIcon ? 'todo-item--excluded' : ''} ${showRowLocked ? 'todo-item--locked' : ''} ${isSelectable ? 'todo-item--selectable' : ''}`}
        data-todo-id={task.id}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
        onClick={handleRowClick}
      >
        {/* Bullet point based on depth */}
        <span className={`todo-item__bullet todo-item__bullet--depth-${Math.min(depth, 4)}`} />

        {/* Fixed-width icon area - contains icon + optional state indicator */}
        {!isSubtask && (
          <div className="todo-item__icon-area">
            {showExcludedIcon ? (
              <div 
                className="todo-item__excluded-indicator"
                title="This path was not chosen — a different decision branch was selected"
              >
                <svg 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  className="todo-item__excluded-icon"
                >
                  <circle cx="12" cy="12" r="9" />
                  <line x1="5.5" y1="5.5" x2="18.5" y2="18.5" />
                </svg>
              </div>
            ) : showLockIcon ? (
              <div 
                className="todo-item__lock-indicator"
                title={lockReason || 'This mission is locked until conditions are met'}
              >
                <svg 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  className="todo-item__lock-icon"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
            ) : null}
            {/* Icon button - only if no state indicator */}
            {!showExcludedIcon && !showLockIcon && elementVisibility.icon !== false && (
              <button
                ref={iconButtonRef}
                className={`todo-item__icon-btn ${task.icon ? 'todo-item__icon-btn--has-icon' : ''}`}
                onClick={handleIconClick}
                onMouseDown={(e) => e.stopPropagation()}
                title="Change icon"
              >
                {task.icon ? (
                  renderIcon(task.icon, { 
                    size: '16px',
                    onCustomIconError: onIconChange ? () => onIconChange(task.id, null) : undefined
                  })
                ) : (hasNestedCampaign || nestedSprint) ? (
                  <span className="todo-item__campaign-icon">
                    <CampaignItemIcon size={16} />
                  </span>
                ) : (
                  <QuestMarker size={16} className="todo-item__quest-marker" />
                )}
              </button>
            )}
          </div>
        )}

        {/* Name */}
        <div className="todo-item__content">
          {/* Hint shown when editing and field is empty */}
          {isEditing && editValue === '' && (
            <span className="todo-item__name-hint">💡 {placeholderHint}</span>
          )}
          
          {isEditing ? (
            <textarea
              ref={inputRef}
              className="todo-item__input"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleFinishEdit}
              onKeyDown={handleKeyDown}
              rows={1}
            />
          ) : (
            <span 
              ref={nameSpanRef}
              className={`todo-item__name ${isPlaceholderName ? 'todo-item__name--placeholder' : ''}`}
              onClick={handleStartEdit}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {taskName || (isDecision ? 'Untitled decision' : 'Untitled mission')}
            </span>
          )}
        </div>


        {/* Status Dropdown (after name, with label) */}
        {!isSubtask && onStatusChange && elementVisibility.status !== false && (
          <div 
            className="todo-item__status-wrapper"
            style={{
              '--status-color': statusConfig.color,
              '--status-bg': `${statusConfig.color}20`,
            }}
          >
            <span className="todo-item__status-label">Status:</span>
            <StatusDropdown
              variant="pill"
              taskId={task.id}
              taskName={task.name || task.title || 'this mission'}
              currentStatus={task.status}
              statuses={statuses}
              allTasks={allTasks}
              isLocked={isLocked}
              lockReason={lockReason}
              blockingDependencies={blockingDependencies}
              parentMissionName={parentMissionName}
              parentCampaignName={parentCampaignName}
              onGoToParentMission={
                parentMissionId && parentSprintId && onNavigateToParentMission
                  ? () => onNavigateToParentMission({ parentSprintId, parentMissionId })
                  : undefined
              }
              onGoToDependency={onFocusTask}
              hasNestedSprint={!!nestedSprint}
              nestedSprintProgress={nestedSprint?.progress}
              statusAutoSync={task.statusAutoSync}
              onStatusChange={onStatusChange}
              onSetStatusAutoSync={onSetStatusAutoSync}
              onAddStatus={onAddStatus}
              onDeleteStatus={onDeleteStatus}
              onRenameStatus={onRenameStatus}
              deliverables={task.deliverables || []}
              onSubmitDeliverable={onSubmitDeliverable}
            />
          </div>
        )}

        {/* Decision choices toggle (for decision nodes) */}
        {!isSubtask && isDecision && onDecisionAnswerChange && (
          <DecisionToggle
            taskId={task.id}
            decisionOptions={decisionOptions}
            decisionAnswer={decisionAnswer}
            onDecisionAnswerChange={onDecisionAnswerChange}
            onOpenOptionsModal={() => setShowDecisionOptionsModal(true)}
          />
        )}

        {/* Waiting for dependencies indicator */}
        {!isSubtask && !isChecked && onDependencyChange && hasBlockingDependencies && elementVisibility.dependencies !== false && (
          <WaitingForIndicator
            task={task}
            allTasks={allTasks}
            blockingDependencies={blockingDependencies}
            onDependencyChange={onDependencyChange}
          />
        )}

        {/* Priority Dropdown */}
        {!isSubtask && onPriorityChange && elementVisibility.priority !== false && (
          <div className="todo-item__dropdown-wrapper">
            <PriorityDropdown
              taskId={task.id}
              currentPriority={task.priority}
              onPriorityChange={onPriorityChange}
            />
          </div>
        )}

        {/* Difficulty Dropdown */}
        {!isSubtask && onDifficultyChange && elementVisibility.difficulty !== false && (
          <div className="todo-item__dropdown-wrapper">
            <DifficultyDropdown
              taskId={task.id}
              currentDifficulty={task.difficulty}
              onDifficultyChange={onDifficultyChange}
            />
          </div>
        )}

        {/* Due date */}
        {dueDateDisplay && elementVisibility.dueDate !== false && (
          <span className={`todo-item__due ${dueDateClass}`}>
            {dueDateDisplay}
          </span>
        )}

        {/* Category and Tags */}
        {!isSubtask && (
          <CategoryTags
            task={task}
            allTags={allTags}
            elementVisibility={elementVisibility}
          />
        )}

        {/* Meta badges (inputs, deliverables, dates) */}
        {!isSubtask && (
          <MetaBadges
            task={task}
            elementVisibility={elementVisibility}
            statuses={statuses}
            nestedSprints={nestedSprints}
            allSprints={allSprints}
          />
        )}

        {/* Custom Fields */}
        {!isSubtask && customFields.length > 0 && (
          <TodoCustomFields
            task={task}
            customFields={customFields}
            onCustomFieldValueChange={onCustomFieldValueChange}
            elementVisibility={elementVisibility}
          />
        )}

        {/* Actions (visible on hover) */}
        {!isSubtask && elementVisibility.actions !== false && (
          <ActionButtons
            task={task}
            allTasks={allTasks}
            visible={showActions}
            onFocusTask={onFocusTask}
            onOpenAttachments={onOpenAttachments}
            onAddSubtask={onAddSubtask}
            onArchive={onArchive}
            onUnarchive={onUnarchive}
            onDeleteClick={() => setShowDeleteConfirm(true)}
            onConditionsChange={onConditionsChange}
            currentView={currentView}
            customViews={customViews}
            activeCustomViewId={activeCustomViewId}
            sourceCampaignId={sourceCampaignId}
            onViewChange={onViewChange}
            onSelectCustomView={onSelectCustomView}
            onGoToTaskInView={onGoToTaskInView}
            elementVisibility={elementVisibility}
            showAttachments={elementVisibility.attachments !== false}
            isParent={isParent}
            onAddMissionToSprint={onAddMissionToSprint}
          />
        )}
      </div>

      {/* Modals */}
      <TodoModals
        showDeleteConfirm={showDeleteConfirm}
        taskName={taskName}
        onConfirmDelete={() => {
          setShowDeleteConfirm(false);
          onDelete?.(task.id);
        }}
        onCancelDelete={() => setShowDeleteConfirm(false)}
      />

      {/* Icon Picker */}
      {showIconPicker && (
        <IconPicker
          currentIcon={task.icon}
          onIconChange={handleIconChange}
          onIconDeleted={() => handleIconChange(null)}
          onClose={closeIconPicker}
          position={iconPickerPosition}
        />
      )}

      {/* Decision Options Modal */}
      {showDecisionOptionsModal && (
        <DecisionOptionsModal
          isOpen={showDecisionOptionsModal}
          options={decisionOptions}
          onOptionsChange={handleDecisionOptionsChange}
          onClose={() => setShowDecisionOptionsModal(false)}
        />
      )}
    </>
  );
});

TodoItem.displayName = 'TodoItem';

export default TodoItem;
 